import Axios, { AxiosResponse } from "axios";
import {
    IPrintRequestPostParams,
    ExportOptions,
    IMapOptions,
    IArcGISDynamicMapServiceLayer,
    IWMSLayer,
    IWMTSLayer,
    IArcGISTiledMapServiceLayer,
    IGraphicsLayer,
    IFeatureLayer,
    PageSize,
    IDocumentExportOptions,
    ServiceLayer,
    IExtent,
    EsriRenderer
} from "@geo/print-service-api";
import { IConfig } from "../config";
import { getMap } from "../GeoLiteMap";
import { DrawType, LayerType } from "../enums";
import { Layer, Tile as TileLayer, Image as ImageLayer, Vector as VectorLayer } from "ol/layer";
import { TileWMS, XYZ, ImageArcGISRest, WMTS, Vector as VectorSource } from "ol/source";
import BaseLayer from "ol/layer/Base";
import { EsriJSON } from "ol/format";
import { Feature } from "ol";

const esriJsonFormat = new EsriJSON();

export const printPost = (
    config: IConfig,
    title: string,
    pageSize: PageSize,
    useLegend: boolean,
    useVectors: boolean,
    printError: CallableFunction
): Promise<void | AxiosResponse<any>> =>
    Axios.post(config.print.url + "?f=json", getRequestData(config, title, pageSize, useLegend, useVectors))
        .then((response) => {
            return response;
        })
        .catch((error) => {
            console.log(error);
            printError();
            return;
        });

const getRequestData = (
    config: IConfig,
    title: string,
    pageSize: PageSize,
    useLegend: boolean,
    useVectors: boolean
): IPrintRequestPostParams => ({
    export: getExportOptions(config, title, pageSize, useLegend, useVectors),
    map: getMapOptions(),
    layers: getLayersOptions()
});

const getExportOptions = (
    config: IConfig,
    title: string,
    pageSize: PageSize,
    legend: boolean,
    useVectors: boolean
): ExportOptions => {
    const type =
        config.print.overview.layer.type === LayerType.TileLayer
            ? "ArcGISTiledMapServiceLayer"
            : config.print.overview.layer.type === LayerType.MapImageLayer
            ? "ArcGISDynamicMapServiceLayer"
            : config.print.overview.layer.type === LayerType.WMTS
            ? "WMTSLayer"
            : "WMSLayer";

    const layer = {
        type,
        url: config.endpoints[config.print.overview.layer.url.endpoint] + config.print.overview.layer.url.url,
        title: ""
    };

    const layoutOptions = {
        title: config.print.staticTitle || title,
        // subTitle: config.print.staticTitle ? title : undefined,
        legend,
        overview: {
            layer: layer as ServiceLayer,
            extent: config.print.overview.extent
        }
    };

    const options: IDocumentExportOptions = {
        type: "document",
        // appid ir pielikts bibliotēkas jaunākajā versijā
        //@ts-ignore
        appid: encodeURI(window.location.host + window.location.pathname),
        grid: "none",
        useVectors,
        pageSize,
        layoutOptions
    };

    return options;
};

const getMapOptions = (): IMapOptions => {
    const ext = getMap().getView().calculateExtent(getMap().getSize());
    const extent: IExtent = {
        xmin: ext[0],
        ymin: ext[1],
        xmax: ext[2],
        ymax: ext[3]
    };

    return {
        dpi: 96,
        extent,
        spatialReference: getMapViewSpatialReference(),
        scale: getMapViewScale()
    };
};

const getLayersOptions = () => {
    const mapLayers = getMap().getLayers().getArray();
    const basemapLayers = mapLayers.filter((l) => l.getZIndex() === 0);
    const tocLayers = mapLayers.filter((l) => l.getZIndex() !== 0);

    return [...getLayersData(basemapLayers), ...getLayersData(tocLayers)];
};

const getLayersData = (mapLayers: BaseLayer[]) => {
    const layers = [];
    mapLayers.map((layer: BaseLayer) => {
        if (!(layer as Layer).isVisible(getMap().getView())) {
            return;
        }

        layers.push(
            layer.get("type") === LayerType.TileLayer
                ? getTileLayerData(layer as TileLayer<XYZ>)
                : layer.get("type") === LayerType.MapImageLayer
                ? getMapImageLayerData(layer as ImageLayer<ImageArcGISRest>)
                : layer.get("type") === LayerType.WMS
                ? getWMSLayerData(layer as TileLayer<TileWMS>)
                : layer.get("type") === LayerType.WMTS
                ? getWMTSLayerData(layer as TileLayer<WMTS>)
                : layer.get("type") === LayerType.FeatureLayer
                ? getDataForFeatureLayer(layer as VectorLayer<VectorSource>)
                : undefined
        );
    });

    return layers.filter((layer) => !!layer);
};

const getTileLayerData = (layer: TileLayer<XYZ>): IArcGISTiledMapServiceLayer => {
    const copyright = layer.get("copyright") && layer.get("copyright") !== "NONE" && layer.get("copyright");
    return {
        type: "ArcGISTiledMapServiceLayer",
        url: layer.get("url"),
        opacity: layer.getOpacity(),
        title: layer.get("title"),
        copyright
    };
};

const getMapImageLayerData = (layer: ImageLayer<ImageArcGISRest>): IArcGISDynamicMapServiceLayer => {
    const visibleLayers = layer.getSource().getParams().LAYERS.split("show:")[1].split(",").map(Number); // LAYERS:"show:2,5,6"
    const copyright = layer.get("copyright") && layer.get("copyright") !== "NONE" && layer.get("copyright");

    return {
        type: "ArcGISDynamicMapServiceLayer",
        url: layer.get("url"),
        opacity: layer.getOpacity(),
        visibleLayers,
        title: layer.get("title"),
        copyright
    };
};

const getWMSLayerData = (layer: TileLayer<TileWMS>): IWMSLayer => {
    const copyright = layer.get("copyright") && layer.get("copyright") !== "NONE" && layer.get("copyright");
    const params = layer.getSource().getParams();

    return {
        type: "WMSLayer",
        url: layer.get("url"),
        opacity: layer.getOpacity(),
        title: layer.get("title"),
        version: layer.get("version") || "1.3.0",
        visibleLayers: [params["LAYERS"]],
        format: params["FORMAT"],
        copyright
    };
};

const getWMTSLayerData = (layer: TileLayer<WMTS>): IWMTSLayer => {
    const copyright = layer.get("copyright") && layer.get("copyright") !== "NONE" && layer.get("copyright");
    const layerName = layer.getSource().getLayer();
    const layerNameString = Array.isArray(layerName) ? layerName[0] : layerName;

    return {
        type: "WMTSLayer",
        url: layer.get("url"),
        opacity: layer.getOpacity(),
        title: layer.get("title"),
        id: layerNameString,
        //@ts-ignore
        layer: layerNameString,
        matrixSet: layer.getSource().getMatrixSet(),
        format: layer.getSource().getFormat(),
        serviceMode: "KVP",
        copyright
    };
};

const getDataForGraphicsLayer = (layer: VectorLayer<VectorSource>) => {
    //: IGraphicsLayer => {
    const features = layer.getSource().getFeatures();

    // features.map((f) => {
    //     const olStyle = getDrawingSymbol(f, f.get("MEASUREMENTSHIDDEN"), f.get("DRAWTYPE"), f.get("COLOR"));
    //     console.log("olStyle", olStyle);
    //     // f.setStyle( );
    // });

    // const geoJsonFeatures = geojsonFormat.writeFeaturesObject(features);
    const esriFeatures = esriJsonFormat.writeFeaturesObject(features);
    const parsedFeatures = features.map((f) => {
        const esriFeature = esriJsonFormat.writeFeatureObject(f);
        esriFeature.geometry.spatialReference = { latestWkid: 3059, wkid: 3059 };
        esriFeature.symbol = getEsriSymbol(f); // { type: "esriSLS", color: [255, 0, 0, 255], width: 2, style: "esriSLSSolid" }

        return esriFeature;
    });
    console.log("esriFeatures", esriFeatures);
    return {
        type: "GraphicsLayer",
        opacity: layer.getOpacity(),
        popupTemplate: null,
        // renderer: {}, // layer.renderer.toJSON(),
        graphics: parsedFeatures
    };
};

// TODO need to finish FeatureLayer params
// main problem is to create renderer data from OL styles
const getDataForFeatureLayer = (layer: VectorLayer<VectorSource>) /*: IFeatureLayer*/ => {
    // at this moment, only drawn features can be printed
    return layer.get("id") === "drawingLayer" && getDataForGraphicsLayer(layer);
};

const getEsriSymbol = (feature: Feature) => {
    const type = feature.get("DRAWTYPE");
    const color = hex2rgbaESRI(feature.get("COLOR")) || [255, 0, 0, 255];
    const style =
        type === DrawType.Polyline
            ? {
                  type: "esriSLS",
                  color,
                  width: 2,
                  style: "esriSLSSolid"
              }
            : type === DrawType.Polygon
            ? {
                  color: hex2rgbaESRI(feature.get("COLOR"), 60) || [255, 0, 0, 255, 50],
                  outline: { type: "esriSLS", color, width: 2, style: "esriSLSSolid" },
                  style: "esriSFSSolid",
                  type: "esriSFS"
              }
            : type === DrawType.PointText
            ? {
                  angle: 0,
                  color,
                  font: { family: "arial", weight: "bold", size: 11 },
                  haloColor: [255, 255, 255, 255],
                  haloSize: 1,
                  horizontalAlignment: "center",
                  kerning: true,
                  lineHeight: 1,
                  lineWidth: 192,
                  rotated: false,
                  text: feature.get("DESCRIPTION"),
                  type: "esriTS",
                  xoffset: 0,
                  yoffset: 0
              }
            : {
                  angle: 0,
                  color,
                  outline: {
                      type: "esriSLS",
                      color,
                      width: 2,
                      style: "esriSLSSolid"
                  },
                  size: 7.5,
                  style: "esriSMSCircle",
                  type: "esriSMS",
                  xoffset: 0,
                  yoffset: 0
              };

    return style;
};

const getMapViewSpatialReference = () => {
    const proj = getMap().getView().getProjection().getCode();
    const projNumber = proj.split(":")[1];

    return { wkid: Number(projNumber), latestWkid: Number(projNumber) /* why is 'latestWkid' needed? */ };
};
//@ts-ignore
const getMapViewScale = (): number => document.mapScale;

const getMapViewZoom = (): number => getMap().getView().getZoom();

const hex2rgbaESRI = (hex: string, alpha = 255): number[] => {
    const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
    return [r, g, b, alpha];
};
